import React, { useState } from 'react';
import ReportItDialog, { reportIssueFeatureEnabled } from '@components/ReportItDialog';
import { FeedbackIcon } from '@icons';
import { withTranslation } from 'react-i18next';
import { isMobileOnly } from 'react-device-detect';
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
} from '@mui/material';

import { usePolicyState } from '@core/myta/components/PolicyModal';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useClientSpecificProfileInformation } from '@hooks/clientSpecificUtils';
import { getAiAssistantLanguageOptions } from '@helpers/assistant';
import {
  Header,
  ModalContainer,
} from '@core/dashboard/components/PopoverModal';
import { ProfileScreensEnum } from '../interfaces';
import AvatarBox from '@components/UserProfileModalNew/AvatarBox';

import { ReactComponent as EmailIcon } from '@public/images/email.svg';
import { ReactComponent as PrivacyIcon } from '@public/images/privacy.svg';
import { ReactComponent as LogoutIcon } from '@public/images/account_exit.svg';
import { ReactComponent as NextIcon } from '@public/images/next.svg';
import { ReactComponent as SettingsIcon } from '@public/images/settings.svg';
import { ReactComponent as ThreeDotsVerticalIcon } from '@public/images/threeDotsVertical.svg';
import { ReactComponent as PersonIcon } from '@public/images/person.svg';

import { IMainMenuList } from './interfaces';
import { useStyles } from './styles';
import { AnalyticsType } from '@interfaces/analytics';
import More from '../More/More';

const MainScreen = ({
  onClose,
  setActiveScreen,
  onOpenLogoutModal,
  t: translate,
}: IMainMenuList) => {
  const classes = useStyles();
  const [moreOpen, setMoreOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const { user, trackAnalytics, appSettings } = useMytaverse();
  const isOnDashboard = !!useMytaverseEvent();

  const languageOptions = getAiAssistantLanguageOptions(appSettings?.aiAssistant);
  const showSettings = isOnDashboard || !!languageOptions?.length;
  const { profileUserName, profileEmail } =
    useClientSpecificProfileInformation();

  const { setOpen: setOpenPrivacy } = usePolicyState();

  const getHeader = () => {
    if (isMobileOnly && moreOpen) {
      return (
        <Header
          onClose={onClose}
          onBack={() => setMoreOpen(false)}
          text={translate('userProfile.more')}
          testId="user-profile"
        />
      );
    } else {
      return <Header onClose={onClose} testId="user-profile" />;
    }
  };

  return (
    <>
      <ModalContainer header={getHeader()} isScrollable={false}>
        {moreOpen && <More onClose={onClose} />}
        {!moreOpen && (
          <Box sx={classes.root}>
            <AvatarBox
              avatar={user.profileImage?.url ?? ''}
              avatarFallback={user.name || profileEmail}
              name={profileUserName}
            />
            <MenuList sx={classes.menu}>
              <MenuItem sx={classes.link}>
                <ListItemIcon>
                  <EmailIcon />
                </ListItemIcon>
                <ListItemText sx={classes.linkText}>
                  {profileEmail}
                </ListItemText>
              </MenuItem>

              {!isMobileOnly && (
                <>
                  <MenuItem
                    sx={classes.link}
                    onClick={() => {
                      setOpenPrivacy(true);
                      trackAnalytics(AnalyticsType.ViewPrivacyPolicy, {
                        ref: 'menu',
                      });
                      onClose();
                    }}
                  >
                    <ListItemIcon>
                      <PrivacyIcon />
                    </ListItemIcon>
                    <ListItemText sx={classes.linkText}>
                      {translate('userProfile.privacy')}
                    </ListItemText>
                    <NextIcon />
                  </MenuItem>

                  {showSettings && (
                    <MenuItem
                      sx={classes.link}
                      onClick={() =>
                        setActiveScreen(
                          isOnDashboard
                            ? ProfileScreensEnum.SETTINGS
                            : ProfileScreensEnum.SETTINGS_LANGUAGE,
                        )
                      }
                      data-testid="user-profile-settings-btn"
                    >
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText sx={classes.linkText}>
                        {translate('userProfile.settings')}
                      </ListItemText>
                      <NextIcon />
                    </MenuItem>
                  )}

                  {reportIssueFeatureEnabled() && (
                    <MenuItem
                      sx={classes.link}
                      onClick={() => {
                        setReportOpen(true);
                      }}
                    >
                      <ListItemIcon>
                        <FeedbackIcon />
                      </ListItemIcon>
                      <ListItemText sx={classes.linkText}>
                        {translate('userProfile.reportItNow')}
                      </ListItemText>
                    </MenuItem>
                  )}
                </>
              )}
              {isMobileOnly && (
                <>
                  <Divider />
                  <Box sx={classes.menuOptionMobile}>
                    {showSettings && (
                      <MenuItem
                        sx={classes.link}
                        onClick={() =>
                          setActiveScreen(
                            isOnDashboard
                              ? ProfileScreensEnum.SETTINGS
                              : ProfileScreensEnum.SETTINGS_LANGUAGE,
                          )
                        }
                        data-testid="user-profile-settings-btn"
                      >
                        <ListItemIcon>
                          <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText sx={classes.linkText}>
                          {translate('userProfile.settings')}
                        </ListItemText>
                      </MenuItem>
                    )}

                    {reportIssueFeatureEnabled() && (
                      <MenuItem
                        sx={classes.link}
                        onClick={() => {
                          setReportOpen(true);
                        }}
                      >
                        <ListItemIcon>
                          <FeedbackIcon />
                        </ListItemIcon>
                        <ListItemText sx={classes.linkText}>
                          {translate('userProfile.reportItNow')}
                        </ListItemText>
                      </MenuItem>
                    )}

                    <MenuItem sx={classes.link} disabled>
                      <ListItemIcon>
                        <PersonIcon />
                      </ListItemIcon>
                      <ListItemText sx={classes.linkText}>
                        {translate('userProfile.myAvatars')}
                      </ListItemText>
                    </MenuItem>
                    <MenuItem
                      sx={classes.link}
                      onClick={() => {
                        setMoreOpen(true);
                      }}
                    >
                      <ListItemIcon>
                        <ThreeDotsVerticalIcon />
                      </ListItemIcon>
                      <ListItemText sx={classes.linkText}>
                        {translate('userProfile.more')}
                      </ListItemText>
                    </MenuItem>
                  </Box>
                </>
              )}

              <Divider />

              <MenuItem
                sx={classes.link}
                onClick={() => {
                  onOpenLogoutModal();
                }}
              >
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText sx={classes.linkText}>
                  {translate('userProfile.logOut')}
                </ListItemText>
              </MenuItem>
            </MenuList>
          </Box>
        )}
      </ModalContainer>
      <ReportItDialog open={reportOpen} onClose={() => setReportOpen(false)} />
    </>
  );
};

export default withTranslation()(MainScreen);
